/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import ActionCreator from '../action-creator';

export interface ChecksumPayload {
  checksum: string;
}

export interface ContentPayload {
  checksum: string;
  markdownContent: string;
}

interface UserEmailPayload {
  userEmail: string;
}

interface UpdateUserLastViewedChecksumPayload {
  userEmail: string;
  checksum: string;
}

export const ActionCreators = {
  RequestLatestChecksumPoll: new ActionCreator<'RequestLatestChecksumPoll', undefined>('RequestLatestChecksumPoll'),
  CancelLatestChecksumPoll: new ActionCreator<'CancelLatestChecksumPoll', undefined>('CancelLatestChecksumPoll'),
  ReceivedLatestChecksumSuccess: new ActionCreator<'ReceivedLatestChecksumSuccess', ChecksumPayload>(
    'ReceivedLatestChecksumSuccess',
  ),
  ReceivedLatestChecksumError: new ActionCreator<'ReceivedLatestChecksumError', any>('ReceivedLatestChecksumError'),

  RequestUserChecksum: new ActionCreator<'RequestUserChecksum', UserEmailPayload>('RequestUserChecksum'),
  ReceivedUserChecksumSuccess: new ActionCreator<'ReceivedUserChecksumSuccess', Partial<ChecksumPayload>>(
    'ReceivedUserChecksumSuccess',
  ),
  ReceivedUserChecksumError: new ActionCreator<'ReceivedUserChecksumError', any>('ReceivedUserChecksumError'),

  RequestWhatsNewContent: new ActionCreator<'RequestWhatsNewContent', undefined>('RequestWhatsNewContent'),
  ReceivedWhatsNewContentSuccess: new ActionCreator<'ReceivedWhatsNewContentSuccess', ContentPayload>(
    'ReceivedWhatsNewContentSuccess',
  ),
  ReceivedWhatsNewContentError: new ActionCreator<'ReceivedWhatsNewContentError', any>('ReceivedWhatsNewContentError'),

  OpenWhatsNewPanel: new ActionCreator<'OpenWhatsNewPanel', undefined>('OpenWhatsNewPanel'),
  CloseWhatsNewPanel: new ActionCreator<'CloseWhatsNewPanel', undefined>('CloseWhatsNewPanel'),

  RequestUpdateUserLastViewedChecksum: new ActionCreator<
    'RequestUpdateUserLastViewedChecksum',
    UpdateUserLastViewedChecksumPayload
  >('RequestUpdateUserLastViewedChecksum'),
  UpdateUserLastViewedChecksumSuccess: new ActionCreator<'UpdateUserLastViewedChecksumSuccess', ChecksumPayload>(
    'UpdateUserLastViewedChecksumSuccess',
  ),
  UpdateUserLastViewedChecksumError: new ActionCreator<'UpdateUserLastViewedChecksumError', any>(
    'UpdateUserLastViewedChecksumError',
  ),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function requestLatestChecksumPoll() {
  return ActionCreators.RequestLatestChecksumPoll.create();
}

export function cancelLatestChecksumPoll() {
  return ActionCreators.CancelLatestChecksumPoll.create();
}

export function requestUserChecksum(userEmail: string) {
  return ActionCreators.RequestUserChecksum.create({ userEmail });
}

export function openWhatsNewPanel() {
  return ActionCreators.OpenWhatsNewPanel.create();
}

export function closeWhatsNewPanel() {
  return ActionCreators.CloseWhatsNewPanel.create();
}

export function updateUserLastViewedChecksum(userEmail: string, checksum: string) {
  return ActionCreators.RequestUpdateUserLastViewedChecksum.create({ userEmail, checksum });
}

export function syncWhatsNewPollingSession(
  isAuthenticated: boolean,
  hasWhatsNewPanelRole: boolean,
  userEmail?: string,
) {
  return (dispatch: any) => {
    if (isAuthenticated && hasWhatsNewPanelRole && userEmail) {
      dispatch(ActionCreators.RequestUserChecksum.create({ userEmail }));
      dispatch(ActionCreators.RequestWhatsNewContent.create());
      dispatch(ActionCreators.RequestLatestChecksumPoll.create());
      return;
    }

    dispatch(ActionCreators.CancelLatestChecksumPoll.create());
  };
}
